// src/context/AuthContext.js

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

import { authService } from '../services/authService';
import userProfileService from '../services/userProfileService';
import { cookieService } from '../services/cookieService';
import apiClient from '../core/apiClient';
import { API_CONFIG } from '../config/config';
import { auth, database } from '../config/firebase';
import { ref, onValue } from 'firebase/database';

// ==================================================================
// Context & Custom Hook
// ==================================================================

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

// ==================================================================
// AuthProvider Component
// ==================================================================

export const AuthProvider = ({ children }) => {
  // ------------------------------
  // États & Références
  // ------------------------------
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [firebaseUserId, setFirebaseUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [linkedAccounts, setLinkedAccounts] = useState(
    () => cookieService.getLinkedAccounts() || []
  );
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const navigate = useNavigate();
  const linkedAccountsFetchedRef = useRef(null);
  const getDeviceLabel = useCallback(() => {
    if (typeof navigator === 'undefined') return null;
    const platform = navigator.platform ? navigator.platform.trim() : '';
    const agent = navigator.userAgent ? navigator.userAgent.trim() : '';
    return [platform, agent].filter(Boolean).join(' · ') || null;
  }, []);
  const buildTrackingPayload = useCallback(
    () => ({
      tracking: {
        last_device: getDeviceLabel(),
        last_login: new Date().toISOString(),
      },
    }),
    [getDeviceLabel]
  );
  const buildProfilePayload = useCallback(
    (profileData = {}) => {
      if (!profileData || Object.keys(profileData).length === 0) {
        return buildTrackingPayload();
      }
      const nowIso = new Date().toISOString();
      const avatarUrl =
        profileData.avatarUrl ||
        profileData.photoURL ||
        firebaseUser?.photoURL ||
        auth.currentUser?.photoURL ||
        null;
    const resolvedEmail =
      profileData.email ||
      user?.email ||
      firebaseUser?.email ||
      auth.currentUser?.email ||
      null;
    const payload = {
      profile: {
        display_name: profileData.displayName?.trim() || undefined,
        avatar_url: avatarUrl || undefined,
        marketing_consent: Boolean(profileData.marketingConsent),
      },
      legal: {
        country: profileData.country || undefined,
        language: profileData.language || undefined,
        user_type: profileData.userType || undefined,
        terms: profileData.acceptTerms
          ? {
              accepted: true,
              timestamp: profileData.termsAcceptedAt || nowIso,
            }
          : undefined,
      },
      tracking: buildTrackingPayload().tracking,
    };
    if (resolvedEmail) {
      payload.email = resolvedEmail;
    }
    return payload;
    },
    [buildTrackingPayload, firebaseUser, user]
  );
  const fetchUserProfile = useCallback(async () => {
    if (!firebaseUserId) {
      setUserProfile(null);
      return null;
    }
    setProfileLoading(true);
    try {
      const profile = await userProfileService.getProfile();
      setUserProfile(profile);
      setProfileError(null);
      return profile;
    } catch (error) {
      console.error('[AuthContext] Failed to fetch user profile:', error);
      setProfileError(error.message || 'Impossible de charger le profil utilisateur');
      return null;
    } finally {
      setProfileLoading(false);
    }
  }, [firebaseUserId]);

  useEffect(() => {
    if (!isAuthenticated || !firebaseUserId) {
      setUserProfile(null);
      return;
    }
    fetchUserProfile();
  }, [isAuthenticated, firebaseUserId, fetchUserProfile]);
  const saveUserProfile = useCallback(
    async (payload) => {
      if (!payload || Object.keys(payload).length === 0) {
        return userProfile;
      }
      try {
        const updated = await userProfileService.updateProfile(payload);
        setUserProfile(updated);
        setProfileError(null);
        return updated;
      } catch (error) {
        console.error('[AuthContext] Failed to update user profile:', error);
        setProfileError(error.message || 'Impossible de mettre à jour le profil');
        throw error;
      }
    },
    [userProfile]
  );

  // État pour les crédits
  const [credits, setCredits] = useState(0);
  const [lastTransaction, setLastTransaction] = useState(null);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [creditsError, setCreditsError] = useState(null);

  // Écouter les changements de crédits (grâce au RTDB si autorisé, sinon fallback API)
  useEffect(() => {
    if (!firebaseUserId) {
      setCredits(0);
      setLastTransaction(null);
      setCreditsLoading(false);
      return;
    }

    setCreditsLoading(true);
    let loadingCleared = false;
    
    // Timeout de sécurité pour éviter un chargement infini (ex: nouveau compte sans données RTDB)
    const safetyTimeout = setTimeout(() => {
      if (!loadingCleared) {
        console.warn('[AuthContext] Credits loading timeout - forcing creditsLoading to false');
        setCreditsLoading(false);
        loadingCleared = true;
      }
    }, 5000);
    
    const creditsRef = ref(database, `users/${firebaseUserId}/credits`);

    console.log('[AuthContext] Subscribing to credits for firebaseUserId:', firebaseUserId);
    const unsubscribe = onValue(
      creditsRef,
      (snapshot) => {
        console.log('[AuthContext] credits onValue triggered for', firebaseUserId);
        clearTimeout(safetyTimeout);
        try {
          const data = snapshot.val();
          console.log('[AuthContext] credits snapshot val:', data);
          if (data) {
            setCredits(data.balance);
            setLastTransaction(data.lastTransaction);
          } else {
            setCredits(0);
          }
          setCreditsError(null);
        } catch (err) {
          console.error('Error in credits listener:', err);
          setCreditsError(err.message);
        } finally {
          if (!loadingCleared) {
            setCreditsLoading(false);
            loadingCleared = true;
          }
        }
      },
      async (error) => {
        console.error('Error in credits listener:', error);
        clearTimeout(safetyTimeout);
        setCreditsError(error.message);
        // Fallback: récupérer le solde via l’API REST
        try {
          const res = await apiClient.get(API_CONFIG.ENDPOINTS.BILLING.STATUS, { timeout: 10000 });
          const value = res?.data?.credits ?? 0;
          setCredits(Number(value));
          setCreditsError(null);
        } catch (err) {
          console.error('[AuthContext] fallback credits fetch failed:', err);
          setCreditsError(err?.message || 'Timeout while fetching credits');
        } finally {
          if (!loadingCleared) {
            setCreditsLoading(false);
            loadingCleared = true;
          }
        }
      }
    );

    return () => {
      clearTimeout(safetyTimeout);
      unsubscribe();
    };
  }, [firebaseUserId]);

  // ------------------------------
  // Fonctions Utilitaires
  // ------------------------------

  /**
   * Récupère les comptes liés depuis le cache local uniquement
   * Note: L'API DeviantArt a été supprimée
   */
  const fetchLinkedAccounts = useCallback(async () => {
    if (!isAuthenticated || !firebaseUserId) return;
    try {
      const cachedAccounts = cookieService.getLinkedAccounts();
      setLinkedAccounts(cachedAccounts || []);
      linkedAccountsFetchedRef.current = firebaseUserId;
    } catch (err) {
      console.error('[AuthContext] Error fetching accounts:', err);
      setLinkedAccounts([]);
    }
  }, [isAuthenticated, firebaseUserId]);

  /**
   * Charger manuellement le solde de crédits depuis l'API (fallback)
   */
  const loadCredits = useCallback(async () => {
    if (!firebaseUserId) return;
    setCreditsLoading(true);
    try {
      const res = await apiClient.get(API_CONFIG.ENDPOINTS.BILLING.STATUS, { timeout: 10000 });
      const value = res?.data?.credits ?? 0;
      setCredits(Number(value));
      setCreditsError(null);
    } catch (err) {
      console.error('[AuthContext] loadCredits error:', err);
      setCreditsError(err?.message || String(err));
    } finally {
      setCreditsLoading(false);
    }
  }, [firebaseUserId]);

  // ==================================================================
  // Fonctions d'Actions d'Authentification
  // ==================================================================

  /**
   * Connexion avec email/mot de passe
   */
  const login = async (email, password) => {
    setLoading(true);
    try {
      const { firebaseUserId } = await authService.login(email, password);
      setIsAuthenticated(true);
      setFirebaseUser(auth.currentUser);
      setFirebaseUserId(firebaseUserId);
      console.log('[AuthContext] firebaseUserId set to:', firebaseUserId);
      try {
        await saveUserProfile(buildTrackingPayload());
      } catch (err) {
        console.warn('[AuthContext] Unable to persist login tracking metadata:', err);
      }
    } catch (error) {
      setIsAuthenticated(false);
      console.error('Error during login:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Méthodes d'aide: récupérer les providers disponibles pour un email
   */
  const getSignInMethodsForEmail = useCallback(async (email) => {
    return authService.getSignInMethods(email);
  }, []);

  /**
   * Réinitialiser le mot de passe par email
   */
  const resetPassword = useCallback(async (email) => {
    return authService.resetPassword(email);
  }, []);

  /**
   * Inscription avec email/mot de passe et KYC minimal
   */
  const register = async ({ email, password, profileData }) => {
    setLoading(true);
    try {
      const { firebaseUserId } = await authService.register({
        email,
        password,
        displayName: profileData?.displayName,
      });
      setIsAuthenticated(true);
      setFirebaseUser(auth.currentUser);
      setFirebaseUserId(firebaseUserId);
      console.log('[AuthContext] User registered with email. firebaseUserId:', firebaseUserId);
      try {
        await saveUserProfile(buildProfilePayload(profileData));
      } catch (error) {
        console.warn('[AuthContext] Unable to initialize user profile after email signup:', error);
      }
    } catch (error) {
      setIsAuthenticated(false);
      console.error("Erreur d'inscription", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Connexion via Google
   */
  const loginWithGoogle = useCallback(async () => {
    setLoading(true);
    try {
      const { firebaseUserId } = await authService.loginWithGoogle();
      setIsAuthenticated(true);
      setFirebaseUser(auth.currentUser);
      setFirebaseUserId(firebaseUserId);
      console.log('[AuthContext] firebaseUserId set to:', firebaseUserId);
      try {
        await saveUserProfile(buildTrackingPayload());
      } catch (err) {
        console.warn('[AuthContext] Unable to persist Google login metadata:', err);
      }
    } catch (error) {
      setIsAuthenticated(false);
      console.error('Erreur de connexion Google', error);
      alert('Erreur de connexion Google : ' + (error.message || 'Veuillez réessayer.'));
    } finally {
      setLoading(false);
    }
  }, [buildTrackingPayload, saveUserProfile]);

  /**
   * Inscription via Google
   */
  const registerWithGoogle = useCallback(async (profileData) => {
    setLoading(true);
    try {
      const { firebaseUserId } = await authService.registerWithGoogle();
      setIsAuthenticated(true);
      setFirebaseUser(auth.currentUser);
      setFirebaseUserId(firebaseUserId);
      console.log('[AuthContext] User registered with Google. firebaseUserId:', firebaseUserId);
      try {
        await saveUserProfile(buildProfilePayload(profileData));
      } catch (error) {
        console.warn('[AuthContext] Unable to initialize user profile after Google signup:', error);
      }
    } catch (error) {
      setIsAuthenticated(false);
      console.error("Erreur d'inscription Google", error);
      alert("Erreur d'inscription Google : " + (error.message || "Veuillez réessayer."));
    } finally {
      setLoading(false);
    }
  }, [buildProfilePayload, saveUserProfile]);

  /**
   * Déconnexion : nettoie l'état et redirige vers la page de login
   */
  const logout = useCallback(async () => {
    try {
      const hasSession = auth.currentUser || cookieService.getUserId();
      if (hasSession) {
        await authService.logout();
      }
    } catch (error) {
      console.error('[AuthContext] Erreur lors de authService.logout():', error);
    } finally {
      setIsAuthenticated(false);
      setUser(null);
      setFirebaseUser(null);
      setFirebaseUserId(null);
      setLinkedAccounts([]);
      setUserProfile(null);
      setProfileError(null);
      setCredits(0);
      setLastTransaction(null);
      cookieService.clearAll();
      linkedAccountsFetchedRef.current = null;
      navigate('/');
    }
  }, [navigate]);

  /**
   * Rafraîchissement du token d'accès
   */
  const refreshAccessToken = useCallback(async () => {
    try {
      if (!auth.currentUser) {
        setIsAuthenticated(false);
        return;
      }
      const newAccessToken = await authService.refreshToken();
      setIsAuthenticated(true);
      return newAccessToken;
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      await logout();
    }
  }, [logout]);

  // ==================================================================
  // Effets (Side Effects)
  // ==================================================================

  /**
   * Intercepteur Axios pour gérer les erreurs liées aux tokens
   */
  useEffect(() => {
    const interceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (
          error.message === 'No access token or refresh token available' ||
          error.message === 'Unable to refresh access token'
        ) {
          await logout();
        }
        return Promise.reject(error);
      }
    );
    return () => {
      apiClient.interceptors.response.eject(interceptor);
    };
  }, [logout]);

  /**
   * Surveille les changements d'état de Firebase (restauration de session)
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
        const storedUserId = cookieService.getUserId();
        const uidToUse = storedUserId || currentUser.uid;
        if (!storedUserId) {
          cookieService.setUserId(uidToUse);
        }
        setFirebaseUserId(uidToUse);
        // Lancer les chargements non bloquants
        fetchLinkedAccounts().catch((err) =>
          console.warn('[AuthContext] fetchLinkedAccounts on auth restore failed:', err)
        );
        loadCredits().catch((err) =>
          console.warn('[AuthContext] loadCredits on auth restore failed:', err)
        );
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setFirebaseUserId(null);
        setLinkedAccounts([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [fetchLinkedAccounts, loadCredits]);

  // Removed redundant effect - credits are now loaded only on auth change and specific events

  // ==================================================================
  // Terms of Service Logic
  // ==================================================================
  const [termsCheck, setTermsCheck] = useState({ mustAccept: false, latestVersion: null, latestDate: null });
  const [billingCheck, setBillingCheck] = useState({ mustFill: false, showModal: false });
  const billingResolveRef = useRef(null);

  const checkTerms = useCallback(async (profile) => {
    if (!profile) return;
    
    const termsInfo = await authService.getTermsInfo();
    if (!termsInfo) return;

    const userTerms = profile.legal?.terms;
    const userVersion = userTerms?.version;
    const userAccepted = userTerms?.accepted;
    const userTimestamp = userTerms?.timestamp;

    const termsDate = new Date(termsInfo.last_updated);
    const userDate = userTimestamp ? new Date(userTimestamp) : new Date(0);

    // Check if not accepted, version mismatch, or accepted before last update
    if (!userAccepted || userVersion !== termsInfo.version || userDate < termsDate) {
        setTermsCheck({
            mustAccept: true,
            latestVersion: termsInfo.version,
            latestDate: termsInfo.last_updated
        });
    } else {
        setTermsCheck({ mustAccept: false, latestVersion: null, latestDate: null });
    }
  }, []);

  const isBillingComplete = useCallback((profile) => {
    if (!profile) return false;
    
    // Check if essential billing info is present
    const billing = profile.billing;
    const hasName = billing?.first_name && billing?.last_name;
    const hasAddress = billing?.address?.line1 && billing?.address?.postal_code && billing?.address?.city && billing?.address?.country;
    
    // If business, check company name
    const isBusiness = billing?.customer_type === 'business';
    const hasCompany = !isBusiness || billing?.company_name;

    return hasName && hasAddress && hasCompany;
  }, []);

  const checkBilling = useCallback((profile) => {
    if (!profile) return;
    const isComplete = isBillingComplete(profile);
    setBillingCheck(prev => ({ ...prev, mustFill: !isComplete }));
  }, [isBillingComplete]);

  // Function to require billing info before a purchase - returns a Promise
  const requireBillingInfo = useCallback(() => {
    return new Promise((resolve) => {
      if (isBillingComplete(userProfile)) {
        resolve(true);
        return;
      }
      // Show the modal and store the resolve function
      billingResolveRef.current = resolve;
      setBillingCheck(prev => ({ ...prev, showModal: true }));
    });
  }, [userProfile, isBillingComplete]);

  // Function to close billing modal (called after form submit or cancel)
  const closeBillingModal = useCallback((success = false) => {
    setBillingCheck(prev => ({ ...prev, showModal: false }));
    if (billingResolveRef.current) {
      billingResolveRef.current(success);
      billingResolveRef.current = null;
    }
  }, []);

  useEffect(() => {
      if (userProfile) {
          checkTerms(userProfile);
          checkBilling(userProfile);
      }
  }, [userProfile, checkTerms, checkBilling]);

  const acceptTerms = async (version) => {
      await authService.acceptTerms(version);
      await fetchUserProfile();
      setTermsCheck({ mustAccept: false, latestVersion: null, latestDate: null });
  };

  // ==================================================================
  // Rendu du Provider
  // ==================================================================

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        firebaseUser,
        firebaseUserId,
        linkedAccounts,
        userProfile,
        profileLoading,
        profileError,
        login,
        loginWithGoogle,
        registerWithGoogle,
        register,
        logout,
        loading: loading || creditsLoading,
        fetchLinkedAccounts,
        refreshAccessToken,
        refreshUserProfile: fetchUserProfile,
        updateUserProfile: saveUserProfile,
        credits,
        lastTransaction,
        creditsLoading,
        creditsError,
        loadCredits,
        getSignInMethodsForEmail,
        resetPassword,
        termsCheck,
        acceptTerms,
        billingCheck,
        requireBillingInfo,
        closeBillingModal,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
